import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmDialog, ConfirmDialogTitle } from '@/components/ConfirmDialog';
import { useDebounceLoading } from '@/hooks/useDebounceLoading';
import { useDeleteEntry, useUpdateEntry } from '@/pages/Schedule/hooks';
import type { DecoratedEntry, DecoratedInstance } from '@/pages/Schedule/types';
import { isRecurringEntry } from '@/pages/Schedule/utils';
import { useToast, Flex, Radio, RadioGroup } from '@/ui';
import type { DraftMode } from '../stores';

type Selection = 'instance' | 'entry';

type Props = {
  isOpen: boolean;
  entry: DecoratedEntry;
  instance?: DecoratedInstance;
  mode?: DraftMode | undefined;
  onDeleteEntry?: () => void;
  onDeleteInstance?: () => void;
  onClose: () => void;
};

export const DeleteEntryConfirmation = ({
  isOpen,
  entry,
  instance,
  mode = 'edit',
  onDeleteEntry,
  onDeleteInstance,
  onClose,
}: Props) => {
  const { deleteEntry, isPending: deleteEntryPending } = useDeleteEntry();
  const { addExclusion, isPending: addExclusionPending } = useUpdateEntry();
  const { t } = useTranslation('common');
  const toast = useToast();

  const isSeries = mode === 'edit' && entry && isRecurringEntry(entry);
  const [selection, setSelection] = useState<Selection>(
    isSeries ? 'instance' : 'entry'
  );

  const title = (entry?.title || t('untitled_entry', { ns: 'common' })).trim();
  const date = instance?.startDate.toFormat('DD');

  // debouncing this state prevents the button from reverting off
  // the loading state while the close animation is playing, which
  // makes for a cleaner animation
  const debouncedIsPending = useDebounceLoading(
    deleteEntryPending || addExclusionPending
  );

  const handleDelete = () => {
    const options = {
      onSuccess: () => toast.notify(`Item deleted!`),
      onError: () => toast.error(`Error deleting item`),
      onSettled: onClose,
    };

    if (selection === 'entry') {
      if (onDeleteEntry) {
        onDeleteEntry();
      } else {
        deleteEntry(entry.id, options);
      }
    }

    if (selection === 'instance' && instance) {
      if (onDeleteInstance) {
        onDeleteInstance();
      } else {
        addExclusion(entry, instance.startDate, options);
      }
    }
  };

  return (
    <ConfirmDialog
      confirmButtonText="Yes, delete"
      isLoading={debouncedIsPending}
      isOpen={isOpen}
      title={
        <ConfirmDialogTitle>
          Are you sure you want to delete <strong>{title}</strong>?
        </ConfirmDialogTitle>
      }
      onCancel={onClose}
      onConfirm={handleDelete}
    >
      {isSeries && (
        <RadioGroup
          colorScheme="red"
          mt="8"
          value={selection}
          onChange={(value) => setSelection(value as Selection)}
        >
          <Flex direction="column">
            <Radio value="instance">Delete {date}</Radio>
            <Radio value="entry">Delete entire series</Radio>
          </Flex>
        </RadioGroup>
      )}
    </ConfirmDialog>
  );
};
